import * as RadixToast from '@radix-ui/react-toast';
import { CheckCircle2, XCircle, AlertTriangle, Info, X } from 'lucide-react';
import { cn } from '@/utils/cn';
import { useToastStore } from '@/store/toastStore';
import type { ToastType } from '@/store/toastStore';

const ICONS: Record<ToastType, typeof CheckCircle2> = {
  success: CheckCircle2,
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
};

const COLOR_CLASSES: Record<ToastType, string> = {
  success: 'border-success bg-success-light text-success-text',
  error: 'border-danger bg-danger-light text-danger-text',
  warning: 'border-warning bg-warning-light text-warning-text',
  info: 'border-primary-mid bg-primary-light text-primary-dark',
};

/** Mount once near the root (see main.tsx). Built on Radix's Toast primitive (research.md §8). */
export function ToastViewport() {
  const toasts = useToastStore((s) => s.toasts);
  const dismiss = useToastStore((s) => s.dismiss);

  return (
    <RadixToast.Provider swipeDirection="right" duration={5000}>
      {toasts.map((t) => {
        const Icon = ICONS[t.type];
        return (
          <RadixToast.Root
            key={t.id}
            onOpenChange={(open) => !open && dismiss(t.id)}
            className={cn(
              'flex items-start gap-3 rounded-lg border p-4 shadow-dropdown',
              'data-[state=open]:animate-slide-up data-[swipe=end]:animate-fade-in',
              COLOR_CLASSES[t.type]
            )}
          >
            <Icon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
            <div className="flex-1">
              <RadixToast.Title className="text-sm font-medium">{t.title}</RadixToast.Title>
              {t.description && <RadixToast.Description className="mt-1 text-sm opacity-90">{t.description}</RadixToast.Description>}
            </div>
            <RadixToast.Close aria-label="Dismiss" className="rounded p-0.5 hover:opacity-70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current">
              <X className="h-4 w-4" aria-hidden="true" />
            </RadixToast.Close>
          </RadixToast.Root>
        );
      })}
      <RadixToast.Viewport className="fixed bottom-0 right-0 z-[100] flex w-full max-w-sm flex-col gap-2 p-4 outline-none" />
    </RadixToast.Provider>
  );
}
